"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelFieldHeight = exports.ModelMutatorHeight = void 0;
const core_api_1 = require("@ikegps/core-api");
const multipoint_1 = require("../../../math/multipoint");
const unit_1 = require("../../../unit");
const collection_field_1 = require("../collection.field");
const marker_drawable_1 = require("../drawable/marker.drawable");
class ModelMutatorHeight extends collection_field_1.ModelMutator {
    /**
     * Remove the state of this drawable
     * *Warning:* You will need to unlink & remove the drawable from the list before destroying the state.
     * @see CollectionModelDrawableStateMutator.remove
     */
    destroyState() {
        this.state.setFieldValue(this.field.id, null);
    }
    clear() {
        this.manual(null);
    }
    manualFromInput(newValue) {
        const { stateValue } = this.field;
        if (!newValue || isNaN(newValue) || !stateValue) {
            this.unlink();
            this.state.setFieldValue(this.field.id, null);
        }
        else {
            const drawableState = this.field.collection.getDrawableState(stateValue.captureId);
            const drawable = drawableState.getDrawable(stateValue.drawable);
            const { x, y } = drawable.calculateM2Px(newValue);
            drawable.moveNoBounds(x, y);
        }
    }
    manual(newValue) {
        if (this.field.isPhotoMeasure()) {
            this.unlink();
        }
        if (newValue == null || isNaN(newValue)) {
            this.state.setFieldValue(this.field.id, null);
        }
        else {
            this.state.setFieldValue(this.field.id, {
                provider: CollectionModelFieldHeight.PROVIDER_MANUAL,
                value: newValue
            });
        }
    }
    spanHeight(captureIds) {
        if (this.field.isPhotoMeasure()) {
            this.unlink();
        }
        if (!Array.isArray(captureIds)) {
            throw new Error(`Field ${this.field.id}: Invalid capture state.`);
        }
        if (captureIds.length !== 2) {
            throw new Error(`Field ${this.field.id}: Invalid capture count "${captureIds.length}".`);
        }
        this.state.setFieldValue(this.field.id, {
            provider: CollectionModelFieldHeight.PROVIDER_SPAN_HEIGHT,
            captures: captureIds.slice()
        });
    }
    drawable(drawable, isManual = false) {
        if (this.field.isPhotoMeasure() || this.field.isPhotoMeasureManual() && !isManual) {
            this.unlink();
        }
        this.state.setFieldValue(this.field.id, {
            provider: isManual ? CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE_MANUAL : CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE,
            drawable: drawable.id,
            captureId: drawable.captureId
        });
        if (!drawable.label.links.some(link => link.id === this.field.id)) {
            drawable.label.linkHeight(this.field);
        }
    }
    unlink() {
        const stateValue = this.field.stateValue;
        // Only unlink if currently linked.
        if (!(stateValue === null || stateValue === void 0 ? void 0 : stateValue.drawable)) {
            return;
        }
        this.destroyState();
        if (stateValue == null || stateValue.drawable == null || stateValue.captureId == null) {
            return;
        }
        const drawableState = this.field.collection.getDrawableState(stateValue.captureId);
        if (drawableState.hasDrawable(stateValue.drawable)) {
            const drawable = drawableState.getDrawable(stateValue.drawable);
            drawable.label.unlink(this.field.id);
        }
    }
    copy(from) {
        var _a, _b;
        if (from.isManual()) {
            this.manual(from.value);
            return true;
        }
        if (from.isSpanHeight()) {
            const state = from.getCaptures().map(capture => capture.objectId);
            this.spanHeight(state);
            return true;
        }
        if (from.isPhotoMeasure() || from.isPhotoMeasureManual()) {
            const fromCollection = from.collection;
            const toCollection = this.field.collection;
            const captureId = (_a = from.stateValue) === null || _a === void 0 ? void 0 : _a.captureId;
            if (!captureId || !((_b = from.stateValue) === null || _b === void 0 ? void 0 : _b.drawable)) {
                return false;
            }
            const drawable = fromCollection.getDrawableState(captureId).getDrawable(from.stateValue.drawable);
            if (fromCollection === toCollection) {
                // Link field to existing marker
                drawable.label.linkHeight(this.field);
                this.drawable(drawable);
                return true;
            }
            if (drawable.label.links.length > 1) {
                const existingNewDrawable = toCollection.getDrawableState(captureId).getAllDrawables()
                    .filter(d => d instanceof marker_drawable_1.CollectionModelDrawableMarker
                    && d.y === drawable.y)[0];
                if (existingNewDrawable) {
                    existingNewDrawable.label.linkHeight(this.field);
                    this.drawable(existingNewDrawable);
                    return true;
                }
            }
            // Create a new marker
            const toDrawableState = toCollection.getDrawableState(captureId);
            const newDrawable = drawable.clone(toDrawableState);
            this.drawable(newDrawable);
            if (toDrawableState.base == null) {
                this.copyBaseMarkers(fromCollection, captureId);
            }
            return true;
        }
        return false;
    }
    // duplicated in vertical.separation.field.ts
    copyBaseMarkers(fromCollection, captureId) {
        const collection = this.field.collection;
        const drawableState = collection.getDrawableState(captureId);
        const fromDrawableState = fromCollection.getDrawableState(captureId);
        const fromBaseMarker = fromDrawableState.base;
        if (fromBaseMarker != null) {
            const { marker: baseMarker } = marker_drawable_1.CollectionModelDrawableMarker.createWithLabel(drawableState, fromBaseMarker.getVector(), fromBaseMarker.label.getVector(), undefined, core_api_1.MarkerType.BASE);
            const fromBaseOffsetMarker = fromDrawableState.baseOffset;
            if (fromBaseOffsetMarker != null) {
                baseMarker.offset = fromBaseMarker.offset;
            }
        }
    }
}
exports.ModelMutatorHeight = ModelMutatorHeight;
class CollectionModelFieldHeight extends collection_field_1.CollectionModelField {
    static isHeightField(f) {
        return f.schema.type === CollectionModelFieldHeight.TYPE;
    }
    get Mutator() {
        if (this._Mutator == null) {
            this._Mutator = new ModelMutatorHeight(this);
        }
        return this._Mutator;
    }
    /* computed */
    get value() {
        const stateValue = this.stateValue;
        if (stateValue == null) {
            return null;
        }
        if (stateValue.provider === CollectionModelFieldHeight.PROVIDER_MANUAL) {
            return stateValue.value;
        }
        if (stateValue.provider === CollectionModelFieldHeight.PROVIDER_SPAN_HEIGHT) {
            const p2pMeasurement = multipoint_1.MultiPoint.Points(this.getCaptures());
            return Math.abs(p2pMeasurement.vertical);
        }
        if (stateValue.provider === CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE) {
            return this.drawable.value;
        }
        if (stateValue.provider === CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE_MANUAL) {
            return this.drawable.value;
        }
        throw new Error(`Field ${this.id}: Invalid Provider type "${stateValue.provider}"`);
    }
    get annotationLabel() {
        //if we're self-title no need to search for field just return displayname
        const fieldId = this.schema.annotationLabelFieldId;
        if (fieldId === this.schema.id) {
            return this.schema.displayName;
        }
        const field = this.findAncestorField(this.parent, fieldId);
        if (!field || !field.schema.type) {
            return this.parent.title;
        }
        switch (field.schema.type) {
            case core_api_1.IkeFieldType.ANCHOR:
                // I think to get the complex values we have to type them first and put them together ourselves
                if (field.hasValue()) {
                    let anchor = field.value;
                    return `${anchor.collectionTool} ${anchor.placement}`;
                }
                return field.schema.displayName;
            case core_api_1.IkeFieldType.DATE:
                let date = field;
                if (!date.hasValue() || date.displayValue == null || date.displayValue === '') {
                    return this.parent.title;
                }
                return date.displayValue;
            case core_api_1.IkeFieldType.FILE_TOOL:
                let fileTool = field;
                if (!fileTool.hasValue() || fileTool.displayValue == null || fileTool.displayValue === '') {
                    return this.parent.title;
                }
                return fileTool.displayValue;
            case core_api_1.IkeFieldType.FORM:
                return field.schema.displayName;
            case core_api_1.IkeFieldType.HEIGHT:
                return field.schema.displayName;
            case core_api_1.IkeFieldType.IMAGE:
                return field.schema.displayName;
            case core_api_1.IkeFieldType.LOCATION:
                let loc = field;
                if (!loc.hasValue() || loc.displayValue == null || loc.displayValue === '') {
                    return this.parent.title;
                }
                let locVal = loc.value;
                return `${locVal.latitude.toFixed(2)},${locVal.longitude.toFixed(2)}`;
            case core_api_1.IkeFieldType.NESTED_LIST:
                if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
                    return this.parent.title;
                }
                return field.displayValue;
            case core_api_1.IkeFieldType.NUMBER:
                if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
                    return this.parent.title;
                }
                return field.displayValue;
            case core_api_1.IkeFieldType.SELECT_LIST:
                if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
                    return this.parent.title;
                }
                return field.displayValue;
            case core_api_1.IkeFieldType.TEXT:
                if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
                    return this.parent.title;
                }
                return field.displayValue;
            case core_api_1.IkeFieldType.TRUE_SIZE:
                return field.schema.displayName;
            case core_api_1.IkeFieldType.VECTOR:
                let vector = field.value;
                if (field.hasValue()) {
                    return `${unit_1.IkeUnit.display(vector.distance, this.distanceUnit, unit_1.IkeUnit.METER, core_api_1.IkeUnitType.DISTANCE, 1)} ${unit_1.IkeUnit.display(vector.bearing, unit_1.IkeUnit.DEGREE, unit_1.IkeUnit.RADIAN, core_api_1.IkeUnitType.ANGLE, 1)}`;
                }
                return field.schema.displayName;
            case core_api_1.IkeFieldType.VERTICAL_SEPARATION:
                return field.schema.displayName;
            case core_api_1.IkeFieldType.YES_NO:
                return field.schema.displayName;
            default:
                return this.parent.title;
        }
    }
    get drawable() {
        const drawableState = this.collection.getDrawableState(this.stateValue.captureId);
        return drawableState.getDrawable(this.stateValue.drawable);
    }
    hasValue() {
        const stateValue = this.stateValue;
        if (stateValue == null) {
            return false;
        }
        return true;
    }
    get provider() {
        const stateValue = this.stateValue;
        if (stateValue == null || stateValue.provider == null) {
            return CollectionModelFieldHeight.PROVIDER_MANUAL;
        }
        return stateValue.provider;
    }
    isManual() {
        return this.provider === CollectionModelFieldHeight.PROVIDER_MANUAL;
    }
    isPhotoMeasure() {
        return this.provider === CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE;
    }
    isPhotoMeasureManual() {
        return this.provider === CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE_MANUAL;
    }
    isSpanHeight() {
        return this.provider === CollectionModelFieldHeight.PROVIDER_SPAN_HEIGHT;
    }
    getMeasurements() {
        if (!this.isPhotoMeasure()) {
            return [];
        }
        const stateValue = this.stateValue;
        if (stateValue == null) {
            return [];
        }
        return [{
                captureId: stateValue.captureId,
                drawableId: stateValue.drawable
            }];
    }
    getOwnedCaptures() {
        var _a, _b;
        if (!this.isSpanHeight()) {
            return [];
        }
        const stateValue = this.stateValue;
        if (stateValue == null) {
            return [];
        }
        return (_b = (_a = stateValue.captures) === null || _a === void 0 ? void 0 : _a.map(c => this.collection.state.getCapture(c)).filter(c => !!c)) !== null && _b !== void 0 ? _b : [];
    }
    getCaptures() {
        return this.getCaptureIds().map(c => this.collection.state.getCapture(c)).filter(c => !!c);
    }
    getOwnedCaptureIds() {
        var _a;
        if (!this.isSpanHeight()) {
            return [];
        }
        const stateValue = this.stateValue;
        if (stateValue == null) {
            return [];
        }
        return (_a = stateValue.captures) !== null && _a !== void 0 ? _a : [];
    }
    getCaptureIds() {
        var _a, _b, _c;
        if (this.isPhotoMeasure()) {
            return ((_a = this.stateValue) === null || _a === void 0 ? void 0 : _a.captureId) ? [(_b = this.stateValue) === null || _b === void 0 ? void 0 : _b.captureId] : [];
        }
        if (!this.isSpanHeight()) {
            return [];
        }
        const stateValue = this.stateValue;
        if (stateValue == null) {
            return [];
        }
        return (_c = stateValue.captures) !== null && _c !== void 0 ? _c : [];
    }
}
exports.CollectionModelFieldHeight = CollectionModelFieldHeight;
CollectionModelFieldHeight.PROVIDER_MANUAL = 'manual';
CollectionModelFieldHeight.PROVIDER_SPAN_HEIGHT = 'spanheight';
CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE = 'photomeasure';
CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE_MANUAL = 'photomeasuremanual';
CollectionModelFieldHeight.TYPE = core_api_1.IkeFieldType.HEIGHT;
//# sourceMappingURL=height.field.js.map